import { useEffect, useRef, useState } from "react";
import { Download, Loader2, Lock, History } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";

export function ResumePreview({ data, template, templates, onTemplate, isPro, onDownload, downloading, saving, snapshot, onExitSnapshot }) {
  const [html, setHtml] = useState("");
  const [scale, setScale] = useState(1);
  const box = useRef(null);
  const timer = useRef(null);
  const snapData = snapshot?.data ? snapshot.data : null;
  const snapText = snapshot && !snapshot.data ? snapshot.text || "" : null;
  const showData = snapData || data;
  const showTemplate = snapData ? snapshot.template || template : template;
  useEffect(() => {
    const fit = () => { if (box.current) setScale(Math.min(1, box.current.clientWidth / 840)); };
    fit();
    const t = setTimeout(fit, 300);
    window.addEventListener("resize", fit);
    return () => { clearTimeout(t); window.removeEventListener("resize", fit); };
  }, []);
  useEffect(() => {
    if (snapText != null) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      api.post("/resume/preview", { template: showTemplate, data: showData }, { responseType: "text" }).then((r) => setHtml(r.data)).catch(() => {});
    }, snapData ? 0 : 450);
    return () => clearTimeout(timer.current);
  }, [showData, showTemplate, snapData, snapText]);

  return (
    <div className="lg:sticky lg:top-20" data-testid="resume-preview-panel">
      {!snapshot && (
        <p className="mb-2 inline-flex items-center gap-2 text-sm font-semibold text-slate-900" data-testid="resume-preview-step">
          <span className="w-5 h-5 rounded-full text-[11px] flex items-center justify-center bg-slate-100 text-slate-500">5</span> Preview &amp; Download
        </p>
      )}
      {snapshot ? (
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="resume-preview-snapshot-banner">
          <p className="text-sm text-amber-900"><History className="w-4 h-4 inline -mt-0.5 mr-1.5" />{snapText != null ? "The uploaded file this review was based on" : "Your resume as it was when this review ran"}{snapshot.reviewed_at && <span className="text-amber-700"> · {new Date(snapshot.reviewed_at).toLocaleDateString()}</span>}</p>
          <button type="button" onClick={onExitSnapshot} className="text-sm font-semibold text-amber-900 underline-offset-2 hover:underline" data-testid="resume-preview-back-current">Back to current resume</button>
        </div>
      ) : (
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1" data-testid="resume-preview-template-pills">
          {templates.map((t) => (
            <button key={t.id} type="button" onClick={() => onTemplate(t.id)} className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${t.id === template ? "bg-slate-900 text-white" : "text-slate-900 hover:bg-slate-100"}`} data-testid={`resume-pill-${t.id}`}>{t.name}</button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400" data-testid="resume-save-status" title="Review every detail before you send it — AI never adds facts, but you own the final check.">{saving ? "Saving…" : "Review every detail before you send it"}</span>
          <Button onClick={onDownload} disabled={downloading} className="bg-primary hover:bg-primary/90" data-testid="resume-download-button">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : isPro ? <Download className="w-4 h-4 mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
            Download PDF{!isPro && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider bg-amber-400 text-slate-950 rounded px-1.5 py-0.5">Premium</span>}
          </Button>
        </div>
      </div>
      )}
      <div ref={box} className="rounded-2xl border border-slate-200 bg-slate-200 overflow-hidden" style={{ height: "min(78vh, 1080px)" }}>
        {snapText != null ? (
          <div className="h-full overflow-auto bg-white p-6" data-testid="resume-preview-upload-text"><pre className="whitespace-pre-wrap font-mono text-xs text-slate-700 leading-relaxed">{snapText}</pre></div>
        ) : html ? <iframe title="Resume preview" srcDoc={html} className="border-0" style={{ width: 840, height: `${100 / scale}%`, transform: `scale(${scale})`, transformOrigin: "top left" }} data-testid="resume-preview-frame" /> : <div className="h-full flex items-center justify-center text-slate-500 text-sm">Loading preview…</div>}
      </div>
    </div>
  );
}
